import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ZoomImage } from './ZoomImage';

/**
 * Product image gallery used by both the product card (compact mode) and the
 * product detail modal (full mode).
 *
 * Props:
 *   images:    string[]  -- list of image URLs
 *   fallback:  string    -- single image URL used when images is empty
 *   alt:       string    -- alt text
 *   compact:   boolean   -- card variant (no arrows/thumbs, hover cycling + dots)
 *   className: string
 */
export const ProductImageGallery = ({
  images,
  fallback,
  alt = '',
  compact = false,
  className = '',
  activeIndex,
  onIndexChange,
}) => {
  // Normalise: use images if non-empty, else [fallback], else []
  const list = useMemo(() => {
    const arr = Array.isArray(images) ? images.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    if (fallback) return [fallback];
    return [];
  }, [images, fallback]);

  // Controlled vs uncontrolled index
  const isControlled = typeof activeIndex === 'number' && typeof onIndexChange === 'function';
  const [internalIndex, setInternalIndex] = useState(0);
  const index = isControlled ? activeIndex : internalIndex;
  const setIndex = (next) => {
    const value = typeof next === 'function' ? next(index) : next;
    if (isControlled) onIndexChange(value);
    else setInternalIndex(value);
  };

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const cardCycleTimer = useRef(null);

  // Reset index when the image set changes (e.g. modal opens for a new product)
  useEffect(() => { setIndex(0); /* eslint-disable-next-line */ }, [list.length, list[0]]);

  // Cleanup hover-cycle timer on unmount (kept before any early return)
  useEffect(() => () => {
    if (cardCycleTimer.current) clearInterval(cardCycleTimer.current);
  }, []);

  if (list.length === 0) {
    return (
      <div className={`bg-[#F5F3F0] flex items-center justify-center ${className}`}>
        <span className="text-xs font-montserrat text-[#8B7D6B] tracking-wider uppercase">No image</span>
      </div>
    );
  }

  const goPrev = (e) => {
    e?.stopPropagation();
    setIndex(i => (i - 1 + list.length) % list.length);
  };
  const goNext = (e) => {
    e?.stopPropagation();
    setIndex(i => (i + 1) % list.length);
  };

  // ---- touch swipe ----
  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    // Lock horizontal scroll on iOS when the gesture is clearly a horizontal swipe
    const dx = Math.abs(e.touches[0].clientX - touchStartX.current);
    const dy = Math.abs(e.touches[0].clientY - (touchStartY.current ?? 0));
    if (dx > dy && dx > 10 && e.cancelable) e.preventDefault();
  };
  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const dx = touchStartX.current - touchEndX.current;
    if (Math.abs(dx) > 40) {
      if (dx > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
  };

  // ---- card mode: cycle through images while hovering (desktop) ----
  const onCardEnter = () => {
    if (!compact || list.length < 2) return;
    cardCycleTimer.current = setInterval(() => {
      setIndex(i => (i + 1) % list.length);
    }, 1100);
  };
  const onCardLeave = () => {
    if (cardCycleTimer.current) {
      clearInterval(cardCycleTimer.current);
      cardCycleTimer.current = null;
    }
    // Keep the last viewed image instead of snapping back to 0 — smoother UX.
  };

  const current = list[index];
  const showControls = !compact && list.length > 1;

  return (
    <div
      className={`relative ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={compact ? onCardEnter : undefined}
      onMouseLeave={compact ? onCardLeave : undefined}
      data-testid="product-image-gallery"
    >
      {/* Main image */}
      <div className="relative w-full h-full overflow-hidden">
        {compact ? (
          // Card mode: simple <img> for fast loading + hover cycling
          list.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={`${alt} ${i + 1}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))
        ) : (
          // Modal mode: full ZoomImage with hover-to-zoom on the active slide
          <ZoomImage src={current} alt={`${alt} ${index + 1}`} className="w-full h-full" />
        )}
      </div>

      {/* Prev / Next arrows (modal mode only) */}
      {showControls && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous image"
            data-testid="gallery-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2C2C2C] rounded-full p-2 shadow-md transition opacity-90 hover:opacity-100 hover:scale-105"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next image"
            data-testid="gallery-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2C2C2C] rounded-full p-2 shadow-md transition opacity-90 hover:opacity-100 hover:scale-105"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Counter pill (modal mode) */}
      {!compact && list.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/55 text-white text-xs font-montserrat tracking-wider px-2.5 py-1 rounded-full pointer-events-none" data-testid="gallery-counter">
          {index + 1} / {list.length}
        </div>
      )}

      {/* Dot indicators (compact card mode) */}
      {compact && list.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none" data-testid="gallery-dots">
          {list.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/60'
              }`}
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.25)' }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Thumbnail strip used by the modal under the main gallery.
 */
export const ProductGalleryThumbs = ({ images, fallback, activeIndex, onSelect, className = '' }) => {
  const list = useMemo(() => {
    const arr = Array.isArray(images) ? images.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    if (fallback) return [fallback];
    return [];
  }, [images, fallback]);

  if (list.length < 2) return null;

  return (
    <div
      className={`flex gap-2 overflow-x-auto pb-1 scrollbar-thin -mx-1 px-1 ${className}`}
      data-testid="gallery-thumbs"
    >
      {list.map((src, i) => (
        <button
          key={src + i}
          type="button"
          onClick={() => onSelect?.(i)}
          aria-label={`Show image ${i + 1}`}
          data-testid={`gallery-thumb-${i}`}
          className={`flex-shrink-0 h-16 w-16 rounded overflow-hidden transition-all border-2 ${
            i === activeIndex
              ? 'border-[#6B4E37] ring-1 ring-[#C4B5A0]'
              : 'border-transparent opacity-70 hover:opacity-100 hover:border-[#D4C4B0]'
          }`}
        >
          <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
};
